<script setup>
// Availability RBAC (fork) — company-wide policy toggle.
//
// ON  -> agents cannot change their own availability; supervisors/admins set
//        it from each advisor's edit dialog (which already has the selector).
// OFF -> standard Chatwoot behaviour: every agent manages their own status.
//
// The card renders only for users allowed to manage the policy (the GET
// probe fails with 401 for everyone else and the card stays hidden).
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { getUserPermissions } from 'dashboard/helper/permissionsHelper';
import AvailabilityManagerAPI from 'dashboard/api/availabilityManager';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

const { t } = useI18n();
const currentUser = useMapGetter('getCurrentUser');
const currentAccountId = useMapGetter('getCurrentAccountId');

const supervisorManaged = ref(false);
const saving = ref(false);

// The GET endpoint is readable by every member; only managers see the card.
const canManage = computed(() => {
  const permissions = getUserPermissions(
    currentUser.value,
    currentAccountId.value
  );
  return (
    permissions.includes('administrator') ||
    permissions.includes('agent_availability_manage')
  );
});

onMounted(async () => {
  try {
    const { data } = await AvailabilityManagerAPI.getAvailabilityPolicy();
    supervisorManaged.value = Boolean(data.supervisor_managed);
  } catch {
    supervisorManaged.value = false;
  }
});

const toggleModel = computed({
  get: () => supervisorManaged.value,
  set: async value => {
    saving.value = true;
    try {
      await AvailabilityManagerAPI.updateAvailabilityPolicy(value);
      supervisorManaged.value = value;
      useAlert(t('AGENT_MGMT.AVAILABILITY_MANAGER.SAVED'));
    } catch {
      useAlert(t('AGENT_MGMT.AVAILABILITY_MANAGER.SAVE_ERROR'));
    } finally {
      saving.value = false;
    }
  },
});
</script>

<template>
  <div v-if="canManage" class="flex flex-col gap-2">
    <div class="flex items-center gap-3">
      <ToggleSwitch v-model="toggleModel" :disabled="saving" />
      <span class="text-body-main text-n-slate-12">
        {{ $t('AGENT_MGMT.AVAILABILITY_MANAGER.TOGGLE_LABEL') }}
      </span>
    </div>
    <p class="text-body-main text-n-slate-11 mb-0">
      {{
        supervisorManaged
          ? $t('AGENT_MGMT.AVAILABILITY_MANAGER.HINT_ON')
          : $t('AGENT_MGMT.AVAILABILITY_MANAGER.HINT_OFF')
      }}
    </p>
  </div>
</template>
